import { chakra, Box, Flex, Text, Icon, Grid, GridItem } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FiBox } from 'react-icons/fi';
import BigNumber from 'bignumber.js';

import type { SocketMessage } from 'lib/socket/types';
import type { Block, BlocksResponse } from 'types/api/block';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import useApiQuery, { getResourceKey } from 'lib/api/useApiQuery';
import useInitialList from 'lib/hooks/useInitialList';
import useIsMobile from 'lib/hooks/useIsMobile';
import { nbsp } from 'lib/html-entities';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';
import { BLOCK } from 'stubs/block';
import { HOMEPAGE_STATS } from 'stubs/stats';
import { Heading } from 'toolkit/chakra/heading';
import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { Tooltip } from 'toolkit/chakra/tooltip';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import BlockEntity from 'ui/shared/entities/block/BlockEntity';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';
import dayjs from 'dayjs';
import LatestBlocksItem from './LatestBlocksItem';

// 自定义的Etherscan风格区块项组件
const EtherscanStyleBlockItem = ({ block, isLoading, animation }: { block: Block; isLoading?: boolean; animation?: string }) => {
  // 计算gas使用百分比
  const gasUsedPercentage = block.gas_used && block.gas_limit
    ? (Number(block.gas_used) / Number(block.gas_limit) * 100).toFixed(2)
    : '0';

  // 格式化交易数量文本
  const txCountText = block.transaction_count === 1 ? 'txn' : 'txns';
  const isMobile = useIsMobile();

  return (
    <Box
      animation={animation}
      borderBottom="1px solid"
      borderColor="gray.100"
      _dark={{ borderColor: "gray.700" }}
      py={3}
      px={{ base: 2, md: 3 }}

    >
      <Grid templateColumns={{
        base: "70px 1fr auto",
        md: "40px 180px 1fr auto"
      }} gap={{ base: 1, md: 3 }} alignItems="flex-start">
        {/* 区块图标 - 电脑端显示，手机端隐藏 */}
        <GridItem display={{ base: "none", md: "flex" }} alignItems="center" justifyContent="center" alignSelf="center">
          <Skeleton loading={isLoading}>
            <Icon as={FiBox} color="#6c757d" boxSize={5} />
          </Skeleton>
        </GridItem>

        {/* 区块号和时间 */}
        <GridItem width={{ base: "70px", md: "auto" }}>
          <Flex direction="column" height="100%" justifyContent="flex-start">
            <Skeleton loading={isLoading} mb={1}>
              <BlockEntity
                number={block.height}
                isLoading={isLoading}
                fontSize="sm"
                fontWeight={500}
                color="#3498db"
                noIcon
              />
            </Skeleton>
            <Skeleton loading={isLoading}>
              <TimeAgoWithTooltip
                timestamp={block.timestamp}
                enableIncrement={!isLoading}
                fontSize="xs"
                color="#77838f"
              />
            </Skeleton>
          </Flex>
        </GridItem>

        {/* 区块信息（中间部分） */}
        <GridItem>
          <Flex direction="column" justifyContent="flex-start" height="100%">
            {!config.features.rollup.isEnabled && !config.UI.views.block.hiddenFields?.miner && (
              <Skeleton loading={isLoading} mb={1}>
                <Flex alignItems="center">
                  <Text color={{ _light: '#212529', _dark: 'gray.400' }} fontSize="xs" width={{ base: "60px", md: "70px" }}>Validator</Text>
                  <AddressEntity
                    address={block.miner}
                    isLoading={isLoading}
                    noIcon
                    noCopy
                    truncation="constant"
                    fontSize="xs"
                    color="#3498db"
                  />
                </Flex>
              </Skeleton>
            )}
            <Skeleton loading={isLoading}>
              <Flex alignItems="center" height={isMobile ? "22px" : "auto"}>
                <Text color="#77838f" fontSize="xs" mr={1}>{block.transaction_count}</Text>
                <Text color="#77838f" fontSize="xs">{txCountText}</Text>
              </Flex>
            </Skeleton>
          </Flex>
        </GridItem>

        {/* 右侧：Gas使用量 */}
        <GridItem justifySelf="right" alignSelf="center">
          <Skeleton loading={isLoading}>
            <Tooltip content="Gas Used">
              <Box
                bg="gray.50"
                _dark={{ bg: "gray.700", color: "gray.200" }}
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="xs"
                fontWeight="medium"
                color="#262b2f"
                textAlign="right"
                display="inline-block"
                whiteSpace="nowrap"
              >
                {isMobile ?
                  `(${gasUsedPercentage}%)` :
                  `${BigNumber(block.gas_used || 0).toFormat()} (${gasUsedPercentage}%)`
                }
              </Box>
            </Tooltip>
          </Skeleton>
        </GridItem>
      </Grid>
    </Box>
  );
};

const LatestBlocks = () => {
  const isMobile = useIsMobile();
  // 设置最大区块数量，无论移动端还是桌面端都显示6条
  const blocksMaxCount = 6;
  const { data, isPlaceholderData, isError } = useApiQuery<'blocks', unknown, Array<Block>>('blocks', {
    queryOptions: {
      placeholderData: {
        items: Array(blocksMaxCount).fill(BLOCK),
        next_page_params: null,
      },
      select: (response: BlocksResponse) => response.items,
      refetchInterval: 4200,
      refetchIntervalInBackground: false,
    },
    queryParams: {
      type: 'block',
    },
  });
  const initialList = useInitialList({
    data: data ?? [],
    idFn: (block) => block.height,
    enabled: !isPlaceholderData,
  });

  const queryClient = useQueryClient();
  const statsQueryResult = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: HOMEPAGE_STATS,
    },
  });

  const handleNewBlockMessage: SocketMessage.NewBlock['handler'] = React.useCallback((payload) => {
    queryClient.setQueryData(getResourceKey('homepage_blocks'), (prevData: Array<Block> | undefined) => {

      const newData = prevData ? [...prevData] : [];

      if (newData.some((block => block.height === payload.block.height))) {
        return newData;
      }

      return [payload.block, ...newData].sort((b1, b2) => b2.height - b1.height).slice(0, blocksMaxCount);
    });
  }, [queryClient, blocksMaxCount]);

  const channel = useSocketChannel({
    topic: 'blocks:new_block',
    isDisabled: isPlaceholderData || isError,
  });
  useSocketMessage({
    channel,
    event: 'new_block',
    handler: handleNewBlockMessage,
  });

  let content;

  if (isError) {
    content = <Text>No data. Please reload the page.</Text>;
  }

  if (data) {
    const dataToShow = data.slice(0, blocksMaxCount);

    const getNetworkValidatorTitle = () => {
      return config.chain.verificationType === 'validation' ? 'Validator' : 'Miner';
    };

    content = (
      <>
        {/* 区块列表 - 使用Etherscan风格 */}
        <Box>
          {dataToShow.map((block, index) => (
            <EtherscanStyleBlockItem
              key={block.height + (isPlaceholderData ? String(index) : '')}
              block={block}
              isLoading={isPlaceholderData}
              animation={initialList.getAnimationProp(block)}
            />
          ))}
        </Box>
        <Link
          href={route({ pathname: '/blocks' })}
          _hover={{ textDecoration: 'none' }}
          display="block"
          width="100%"
        >
          <Flex
            justifyContent="center"
            py={3}
            borderTopWidth="1px"
            borderColor="gray.100"
            bg="gray.50"
            _dark={{ bg: "gray.700", borderColor: "gray.700" }}
            _hover={{ bg: "gray.100", _dark: { bg: "gray.600" } }}
            transition="background 0.2s"
          >
            <Text
              textStyle="sm"
              color="gray.600"
              _dark={{ color: "gray.200" }}
              fontWeight="medium"
            >
              View all blocks
            </Text>
          </Flex>
        </Link>
      </>
    );
  }

  return (
    <Box width="100%" flexShrink={0} bgColor="white" _dark={{ bgColor: "gray.800" }} borderRadius={{ base: 0, md: "md" }} overflow="hidden"
      boxShadow={{
        _light: "0 0.5rem 1.2rem rgba(189, 197, 209, 0.2)",
        _dark: "0 0.5rem 1.2rem rgba(20, 20, 21, 0.4)"
      }} border="1px solid" borderColor={{ _light: '#e9ecef', _dark: 'whiteAlpha.200' }}
    >
      <Flex justifyContent="space-between" alignItems="center" p={{ base: 3, md: 4 }} borderBottomWidth="1px" borderColor="gray.100" _dark={{ borderColor: "gray.700" }}>
        <Heading level="3" fontSize="lg" fontWeight="semibold" color={{ _light: '#212529', _dark: 'gray.400' }}>Latest blocks</Heading>
      </Flex>
      <Box>
        {content}
      </Box>
    </Box>
  );
};

export default LatestBlocks;
